/**
 * Centralized Query Cache Configuration
 * 
 * This file contains optimized cache settings for React Query
 * to minimize database I/O and improve application performance.
 */

/** Cache timing constants (in milliseconds) */
export const CACHE_TIMES = {
  /** Frequently changing data - short cache */
  REALTIME: {
    staleTime: 1 * 60 * 1000,      /** 1 minute */
    gcTime: 5 * 60 * 1000,         /** 5 minutes */
  },
  
  /** Standard data - medium cache */
  STANDARD: {
    staleTime: 5 * 60 * 1000,      /** 5 minutes */
    gcTime: 30 * 60 * 1000,        /** 30 minutes */
  },
  
  /** Moderately static data - longer cache */
  MODERATE: {
    staleTime: 10 * 60 * 1000,     /** 10 minutes */
    gcTime: 60 * 60 * 1000,        /** 1 hour */
  },
  
  /** Rarely changing data - long cache */
  STATIC: {
    staleTime: 30 * 60 * 1000,     /** 30 minutes */
    gcTime: 2 * 60 * 60 * 1000,    /** 2 hours */
  },
  
  /** Reference data (categories, schools, etc.) - very long cache */
  REFERENCE: {
    staleTime: 60 * 60 * 1000,     /** 1 hour */
    gcTime: 4 * 60 * 60 * 1000,    /** 4 hours */
  },

  /** Platform config / decoration catalog / upload catalog — rarely changes */
  CONFIG: {
    staleTime: 24 * 60 * 60 * 1000,  /** 24 hours */
    gcTime: 7 * 24 * 60 * 60 * 1000, /** 7 days */
  },
} as const;

/** Specific cache configurations by data type */
export const QUERY_CONFIG = {
  /** Design requests - core data, needs to stay relatively fresh */
  designRequests: CACHE_TIMES.STANDARD,
  
  /** Request products - tied to design requests */
  requestProducts: CACHE_TIMES.STANDARD,
  
  /** Revisions - change less frequently after creation */
  revisions: CACHE_TIMES.STANDARD,
  
  /** Work sessions - can be cached longer, stats don't need instant updates */
  workSessions: CACHE_TIMES.MODERATE,
  
  /** Designer stats - computed data, can be cached longer */
  designerStats: CACHE_TIMES.MODERATE,
  
  /** Admin stats - aggregated data */
  adminStats: CACHE_TIMES.MODERATE,
  
  /** Clients list - changes less frequently */
  clients: CACHE_TIMES.STANDARD,
  
  /** Designers list - rarely changes */
  designers: CACHE_TIMES.STATIC,
  
  /** Categories/Sub-items - reference data, rarely changes */
  categories: CACHE_TIMES.REFERENCE,
  
  /** Schools - reference data */
  schools: CACHE_TIMES.REFERENCE,
  
  /** Organizations - reference data */
  organizations: CACHE_TIMES.REFERENCE,
  
  /** Invoices - standard business data */
  invoices: CACHE_TIMES.STANDARD,
  
  /** Orders - standard business data */
  orders: CACHE_TIMES.STANDARD,
  
  /** User profiles - rarely changes */
  profiles: CACHE_TIMES.STATIC,
  
  /** User roles - very rarely changes */
  userRoles: CACHE_TIMES.STATIC,
  
  /** Activity logs - append-only, safe to cache */
  activityLogs: CACHE_TIMES.MODERATE,
  
  /** Theme settings - rarely changes */
  themeSettings: CACHE_TIMES.STATIC,
  
  /** Integrations (Zoho) - rarely changes */
  integrations: CACHE_TIMES.STATIC,

  platformConstants: CACHE_TIMES.MODERATE, // switches must refresh within 10 minutes
  decorationCatalog: CACHE_TIMES.CONFIG,
  uploadCatalog: CACHE_TIMES.CONFIG,
} as const;

/** Batch size for paginated queries */
export const BATCH_SIZES = {
  default: 100,
  large: 200,
  small: 50,
} as const;

/** Page sizes for UI pagination — default first page is 25 */
export const PAGE_SIZES = {
  default: 25,
  compact: 5,
  expanded: 25,
} as const;
